/**
 * Router Guards -- Product Management Portal
 */
import { START_LOCATION, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore, usePermissionsStore } from '@/stores';
import type { ModulePermissions } from '@/core/services/permissions.service';

const LAST_ACCESSIBLE_ROUTE_KEY = 'tms-product-last-accessible-route';
const INVALID_ROLE_VALUES = new Set(['NO_ROLE', 'DEFAULT', 'DEFAULT_ROLE']);

const MODULE_ROUTE_FALLBACKS: Array<{ moduleKey: keyof ModulePermissions; path: string }> = [
  { moduleKey: 'productManagement', path: '/products' },
  { moduleKey: 'tenantManagement', path: '/onboarding' },
  { moduleKey: 'userManagement', path: '/users' },
  { moduleKey: 'recall', path: '/recall' },
  { moduleKey: 'auditLogs', path: '/audit-logs' },
  { moduleKey: 'enterprisesManagement', path: '/enterprises' },
  { moduleKey: 'settings', path: '/settings' },
];

function hasNoAccessRole(parsedRoles: unknown): boolean {
  if (!Array.isArray(parsedRoles)) return true;

  const normalizedRoles = parsedRoles
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().toUpperCase())
    .filter((role) => role.length > 0);

  if (normalizedRoles.length === 0) return true;

  return normalizedRoles.every((role) => INVALID_ROLE_VALUES.has(role));
}

function getFirstAccessibleRoute(): string | null {
  const permissionsStore = usePermissionsStore();

  const firstAccessible = MODULE_ROUTE_FALLBACKS.find((item) => permissionsStore.isModuleAllowed(item.moduleKey));
  return firstAccessible?.path ?? null;
}

function getNoAccessRoute(): string {
  return '/no-access';
}

function persistLastAccessibleRoute(route: RouteLocationNormalized): void {
  if (typeof sessionStorage === 'undefined') return;

  const path = route.path;
  if (path === '/login' || path === getNoAccessRoute()) return;

  // Store only the path — never fullPath — to prevent query-string tokens,
  // IDs, or PII from being written to sessionStorage.
  sessionStorage.setItem(LAST_ACCESSIBLE_ROUTE_KEY, path);
}

function getLastAccessibleRoute(): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  const route = sessionStorage.getItem(LAST_ACCESSIBLE_ROUTE_KEY);
  if (!route) return null;
  if (route === '/login' || route === getNoAccessRoute()) return null;
  return route;
}

/**
 * Authentication Guard
 * Redirects to /login if the route requires auth and the user is not authenticated.
 */
export function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const authStore = useAuthStore();
  const permissionsStore = usePermissionsStore();

  // Direct entry to /login (e.g. an invite email link opened on a shared
  // workstation) while a session already exists — send the user to a
  // dedicated page (ActiveSessionView) that signs the previous user out
  // automatically and then moves on to the login form, instead of silently
  // redirecting to the first accessible route or ending the session here. `from ===
  // START_LOCATION` is only true on the first navigation after a page
  // load, which distinguishes this from in-app navigation (e.g. the
  // browser Back button after signing in) — that case must keep the
  // redirect-to-first-accessible-route behavior below.
  // Match on the route NAME, not the literal path: Vue Router's default non-strict,
  // case-insensitive matching resolves '/login/' and '/LOGIN' to the login route
  // while leaving `to.path` as typed, so a path comparison would let those
  // variants render the login form over the existing session.
  if (to.name === 'login' && from === START_LOCATION && authStore.isAuthenticated) {
    next({ name: 'active-session', query: to.query });
    return;
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);
  const requiredModulePermission = to.matched
    .map((record) => record.meta.modulePermission as (keyof ModulePermissions | undefined))
    .filter((value): value is keyof ModulePermissions => !!value)
    .at(-1);

  if (requiresAuth && !authStore.isAuthenticated) {
    // Use to.path — fullPath is excluded to prevent tokens, IDs, or PII in
    // query strings from leaking into browser history and server access logs.
    next({ path: '/login', query: { redirect: to.path } });
    return;
  }

  // Block access to authenticated routes until first-time MFA setup is complete
  if (
    authStore.isAuthenticated &&
    authStore.mfaSetupRequired &&
    to.path !== '/mfa-setup' &&
    // The /login redirect above lands here; without this exemption a session
    // with a pending MFA setup would be bounced straight into that user's MFA
    // flow, skipping the sign-out-or-continue choice.
    to.name !== 'active-session'
  ) {
    next({ path: '/mfa-setup', replace: true });
    return;
  }

  if (
    authStore.isAuthenticated
    && requiresAuth
    && to.path !== '/no-access'
    && to.path !== '/logout'
    && hasNoAccessRole(authStore.parsedRoles)
  ) {
    next({ path: '/no-access', replace: true });
    return;
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    if (permissionsStore.isLoaded) {
      next({ path: getFirstAccessibleRoute() ?? getNoAccessRoute() });
      return;
    }

    permissionsStore.fetchPermissions()
      .then(() => {
        next({ path: getFirstAccessibleRoute() ?? getNoAccessRoute() });
      })
      .catch(() => {
        next({ path: getNoAccessRoute() });
      });
    return;
  }

  const isNoAccessRoute = to.path === getNoAccessRoute();

  if (!requiredModulePermission) {
    if (requiresAuth && authStore.isAuthenticated && !isNoAccessRoute) {
      persistLastAccessibleRoute(to);
    }
    next();
    return;
  }

  const enforceModulePermission = () => {
    if (permissionsStore.isModuleAllowed(requiredModulePermission)) {
      if (!isNoAccessRoute) {
        persistLastAccessibleRoute(to);
      }
      next();
      return;
    }

    const fallbackRoute = getLastAccessibleRoute() ?? getFirstAccessibleRoute();
    if (!fallbackRoute) {
      if (to.path === getNoAccessRoute()) {
        next();
        return;
      }
      next({ path: getNoAccessRoute() });
      return;
    }

    if (fallbackRoute === to.path || fallbackRoute === to.fullPath) {
      next();
      return;
    }

    next(fallbackRoute);
  };

  // If permissions were never loaded for this session, fetch once before enforcing.
  // fetchPermissions() dedupes in-flight calls, so this safely awaits concurrent requests too.
  if (!permissionsStore.isLoaded) {
    permissionsStore.fetchPermissions()
      .then(() => enforceModulePermission())
      .catch(() => {
        next({ path: getNoAccessRoute() });
      });
    return;
  }

  enforceModulePermission();
}

/**
 * Navigation Log Guard
 * Unconditionally advances navigation (used alongside authGuard for debug logging).
 */
export function navigationLogGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  next();
}
